/**
 * A 表 · 认证域 —— 9 个端点全部已建成，可直接对接。
 *
 * 令牌的写入统一在这一层做：领匿名 / 登录 / 续期成功就落 tokenStore，登出一定清干净。
 * 散在界面里写会漏 —— 漏了的表现是「登录成功但下一屏又要登录」。
 */
import {
  authResponseSchema,
  sendCodeResponseSchema,
  userSchema,
  voidDataSchema,
  type AdminLoginRequest,
  type AuthResponse,
  type LoginRequest,
  type SendCodeRequest,
  type SendCodeResponse,
  type SetPasswordRequest,
  type UpdateMeRequest,
  type User,
} from '@cione/contracts'
import { toExpiresAt, type Tokens } from './token-store'
import type { HttpClient } from './http'

export interface AuthApi {
  /** A1 领匿名令牌。⚠ 网关白名单，不带令牌。 */
  anonymous(): Promise<AuthResponse>
  /** A2 发短信验证码。⚠ 回的 `cooldownSeconds` 要用来倒计时禁用按钮。 */
  sendCode(request: SendCodeRequest): Promise<SendCodeResponse>
  /** A3 登录。⚠ 带着匿名令牌调 = 要求合并影子用户，见下方注释。 */
  login(request: LoginRequest): Promise<AuthResponse>
  /** A4 管理员登录(M5 管理后台用)。 */
  adminLogin(request: AdminLoginRequest): Promise<AuthResponse>
  /** A5 续期。⚠ **不写 tokenStore** —— 写入由单飞刷新统一做，避免两处各写一遍。 */
  callRefresh(refreshToken: string): Promise<AuthResponse>
  /** A6 登出。⚠ 无论服务端成不成功，本地令牌一定清掉。 */
  logout(): Promise<void>
  /** A7 我是谁。 */
  me(): Promise<User>
  /** A8 改资料。⚠ 字段为 null 表示「不改」，不是「清空」。 */
  updateMe(request: UpdateMeRequest): Promise<User>
  /** A9 改密。⚠ 需 `bind` 场景验证码；**匿名禁止**(网关拦)。 */
  setPassword(request: SetPasswordRequest): Promise<void>
}

export function createAuthApi(http: HttpClient, now: () => number = Date.now): AuthApi {
  function persist(auth: AuthResponse): AuthResponse {
    const tokens: Tokens = {
      accessToken: auth.tokens.accessToken,
      refreshToken: auth.tokens.refreshToken,
      expiresAt: toExpiresAt(auth.tokens.expiresIn, now()),
    }
    http.tokenStore.set(tokens)
    return auth
  }

  return {
    async anonymous() {
      return persist(
        await http.request({
          method: 'POST',
          path: '/api/auth/anonymous',
          schema: authResponseSchema,
          auth: 'none',
        }),
      )
    },

    sendCode(request) {
      return http.request({
        method: 'POST',
        path: '/api/auth/sms/send',
        body: request,
        schema: sendCodeResponseSchema,
        auth: 'none',
      })
    },

    async login(request) {
      return persist(
        await http.request({
          method: 'POST',
          path: '/api/auth/login',
          body: request,
          schema: authResponseSchema,
          // ⚠⚠ 'optional' 是**故意**的：本地有匿名令牌就带上，那是在要求合并影子用户。
          //    不合并的话，匿名期搜的机票报价快照还挂在影子 id 上，登录后取料直接被拒，
          //    用户被迫重搜(16 秒起)。这是 A3 最容易漏的一条。
          auth: 'optional',
        }),
      )
    },

    async adminLogin(request) {
      return persist(
        await http.request({
          method: 'POST',
          path: '/api/auth/admin/login',
          body: request,
          schema: authResponseSchema,
          auth: 'none',
        }),
      )
    },

    callRefresh(refreshToken) {
      return http.request({
        method: 'POST',
        path: '/api/auth/refresh',
        body: { refreshToken },
        schema: authResponseSchema,
        // ⚠ 白名单端点，且**不能带 access** —— 它已经过期了，带上只会多一次无谓的 401
        auth: 'none',
        // ⚠⚠ 必须关掉：本请求就在单飞刷新内部，401 再触发刷新就是等自己，死锁
        allowRefresh: false,
      })
    },

    async logout() {
      const refreshToken = http.tokenStore.get()?.refreshToken
      try {
        if (refreshToken) {
          await http.request({
            method: 'POST',
            path: '/api/auth/logout',
            body: { refreshToken },
            schema: voidDataSchema,
          })
        }
      } finally {
        // ⚠ 服务端失败也要清：留着废令牌只会让下一次请求走一遍 401 → 刷新 → 再失败
        http.tokenStore.set(null)
      }
    },

    me() {
      return http.request({ method: 'GET', path: '/api/auth/me', schema: userSchema })
    },

    updateMe(request) {
      return http.request({
        method: 'PATCH',
        path: '/api/auth/me',
        body: request,
        schema: userSchema,
      })
    },

    async setPassword(request) {
      await http.request({
        method: 'PUT',
        path: '/api/auth/password',
        body: request,
        schema: voidDataSchema,
      })
    },
  }
}
